import React, { useState, useRef, useEffect } from 'react';
import { Mail, Lock, Sun, Moon, Loader2, Phone, ChevronDown } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { supabase } from '../../lib/supabase';
import { translations, languages } from '../../translations';
import { Auth } from './Auth';
import { useNavigate } from 'react-router-dom';
import { phoneCodes } from '../../data/phoneCodes';

interface OnboardingProps {
  isDarkMode: boolean;
  language: string;
  onThemeChange?: (isDark: boolean) => void;
  setLanguage: (lang: string) => void;
  plans: any[];
}

export function Onboarding({ isDarkMode, language, onThemeChange, setLanguage, plans }: OnboardingProps) {
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneCode, setPhoneCode] = useState('+33');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});
  const t = translations[language as keyof typeof translations];

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Add spaces every 2 digits
    const formatted = digits.replace(/(\d{2})(?=\d)/g, '$1 ');
    
    return formatted;
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };
  useEffect(() => {
    // Extract UTM parameters from URL
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    
    // Collect all UTM parameters
    params.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        utm[key] = value;
      }
    });

    // Store UTM parameters if any exist
    if (Object.keys(utm).length > 0) {
      setUtmParams(utm);
    }
  }, []);

  // Listen for auth state changes to detect Google signup
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Check if this is a new user (Google signup) - compare timestamps with tolerance
        const createdAt = new Date(session.user.created_at);
        const lastSignIn = new Date(session.user.last_sign_in_at || session.user.created_at);
        const timeDiff = Math.abs(lastSignIn.getTime() - createdAt.getTime());
        const isNewUser = timeDiff < 5000; // 5 seconds tolerance
        
        if (isNewUser && session.user.email) {
          // Create profile with UTM data for new Google signup
          try {
            // First create the profile with UTM data
            const { error: profileError } = await supabase
              .from('profiles')
              .upsert({
                id: session.user.id,
                utm: Object.keys(utmParams).length > 0 ? utmParams : null
              });

            if (profileError) {
              console.error('Error creating profile:', profileError);
            }

            // Then send webhook for new Google signup
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/signup-webhook`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                email: session.user.email,
                utm: Object.keys(utmParams).length > 0 ? utmParams : null,
                signup_method: 'google'
              }),
            });
            console.log('Google signup webhook sent for:', session.user.email);
          } catch (webhookError) {
            console.error('Failed to send Google signup notification:', webhookError);
          }
        }
        
        // Redirect to add account page
        navigate('/add-account');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (showLogin) {
    return (
      <Auth 
        isDarkMode={isDarkMode} 
        language={language} 
        onThemeChange={onThemeChange}
        setLanguage={setLanguage}
        onBack={() => setShowLogin(false)}
      />
    );
  }

  const handleSignup = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!email || !password || !phoneNumber) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      if (!acceptedTerms) {
        setError(t.terms_required);
        return;
      }

      if (!captchaToken) {
        setError('Please complete the captcha verification');
        return;
      }

      // Combine phone code and number
      const fullPhoneNumber = phoneCode + phoneNumber.replace(/^0+/, '');

      // Sign up user
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            utm: Object.keys(utmParams).length > 0 ? utmParams : null,
            phone: fullPhoneNumber
          }
        }
      });

      if (signUpError) throw signUpError;
      if (!signUpData.session) {
        throw new Error('Failed to create session after signup.');
      }

      // Create profile with phone number
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: signUpData.user.id,
          phone: fullPhoneNumber,
          utm: Object.keys(utmParams).length > 0 ? utmParams : null
        });

      if (profileError) {
        console.error('Error creating profile:', profileError);
      }

      // Redirect to add account page
      navigate('/add-account');

    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message);
      // Reset captcha on error
      captchaRef.current?.resetCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setError(null);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message);
      setIsGoogleLoading(false);
    }
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  const handleCaptchaError = (err: Error) => {
    console.error('Captcha error:', err);
    setError('Failed to verify captcha. Please try again.');
    setCaptchaToken(null);
  };

  const getWelcomeImage = () => {
    switch (language) {
      case 'FR':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/FR.png';
      case 'EN':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
      case 'ES':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/ES.png';
      case 'RU':
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/RU.png';
      default:
        return 'https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/welcome/EN.png';
    }
  };

  return (
    <div className={`min-h-screen flex flex-col md:flex-row ${isDarkMode ? 'bg-[#121212]' : 'bg-gray-50'}`}>
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 flex items-start justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t.grow_instagram}
            </h1>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.automate_growth}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading}
            className={`w-full py-2.5 px-4 border rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors mb-6 ${
              isDarkMode
                ? 'bg-[#2b2b2b] border-[#3b3b3b] text-gray-200 hover:bg-[#333333]'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isGoogleLoading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
            )}
            {t.continue_with_google}
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className={`w-full border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'}`}></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className={`px-2 ${isDarkMode ? 'bg-[#121212] text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                {t.or}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.email}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.email_placeholder}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm border ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                />
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Téléphone
              </label>
              <div className="flex gap-2">
                <div className="relative">
                  <select
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value)}
                    className={`rounded-lg pl-8 pr-8 py-2.5 text-sm appearance-none min-w-[100px] ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-white text-gray-900 border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  >
                    {phoneCodes.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <Phone size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="06 12 34 56 78"
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  required
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1.5 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.password}
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.password_placeholder}
                  className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm border ${
                    isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                      : 'bg-white text-gray-900 border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                />
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <HCaptcha
                ref={captchaRef}
                sitekey="8fb605f4-8808-4eb8-bbb0-0032d922a52e"
                onVerify={handleCaptchaVerify}
                onExpire={handleCaptchaExpire}
                onError={handleCaptchaError}
                theme={isDarkMode ? 'dark' : 'light'}
                size="normal"
              />
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                />
              </div>
              <label
                htmlFor="terms"
                className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
              >
                {t.accept_terms}{' '}
                <a href="#" className="text-orange-500 hover:underline">
                  {t.terms_of_service}
                </a>{' '}
                {t.and}{' '}
                <a href="#" className="text-orange-500 hover:underline">
                  {t.privacy_policy}
                </a>
              </label>
            </div>
          </div>

          <button
            onClick={handleSignup}
            disabled={isLoading || !captchaToken}
            className="w-full mt-6 bg-orange-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {t.please_wait}
              </>
            ) : (
              t.sign_up
            )}
          </button>

          <p className="text-center mt-4 text-sm">
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
              {t.already_have_account}{' '}
            </span>
            <button
              onClick={() => setShowLogin(true)}
              className="text-orange-500 hover:text-orange-600 font-medium"
            >
              {t.sign_in}
            </button>
          </p>

          {/* Language and Theme Controls */}
          <div className="fixed bottom-6 left-6 flex items-center gap-4">
            {/* Language Selector */}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`rounded-lg pl-3 pr-8 py-2 text-sm appearance-none ${
                isDarkMode
                  ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                  : 'bg-white text-gray-900 border border-gray-200'
              }`}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>

            {/* Theme Toggle */}
            <button
              onClick={() => onThemeChange?.(!isDarkMode)}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'bg-[#2b2b2b] text-gray-400 hover:text-gray-200'
                  : 'bg-white text-gray-600 hover:text-gray-900 shadow-sm border border-gray-200'
              }`}
            >
              {isDarkMode ? (
                <Sun size={20} strokeWidth={2} />
              ) : (
                <Moon size={20} strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Welcome image */}
      <div 
        className="hidden md:block md:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: `url("${getWelcomeImage()}")`
        }}
      />
    </div>
  );
}